import { useTheme, Box, Avatar, Dialog } from '@mui/material';
import { useEffect, useState } from 'react';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../components/exo/ExoTableToolbar';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoTable from '../../components/exo/ExoTable';
import { OrderStatus } from '../../components/special/Status';
import NewOrder from '../../components/new/NewOrder';
import ExtendOrder from './extendOrder';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/special/Header';
import ExoTableRecipient from '../../components/exo/ExoTableRecipient';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import { recipientTypeMap } from '../../components/routing/routingMaps';
import DateLabel from '../../components/label/DateLabel';
import { LeadStatusSelector } from '../leads/status/LeadStatusSelector';
import { updateOrCreate } from '../../components/special/updateOrCreate';

const Orders = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const navigate = useNavigate();

  const { apiData, isLoading, setApiData } = useFetch('orders');
  const { offerId } = useParams();

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleStatusClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    const request = { status: status.status };

    DBRequest({
      config,
      path: `orders/${item.id}/status`,
      method: API_PATCH(request),
      onResponse: () =>
        setApiData({
          ...apiData,
          data: updateOrCreate(apiData.data, {
            ...item,
            status: status.status,
          }),
        }),
    });
  }

  // table
  const Columns = [
    {
      header: t('Nr'), // header of column
      key: 'nr', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <Box className="monospace whitespace-nowrap">
          {row.nr ?? t('Draft')}
        </Box>
      ),
    },
    {
      header: t('Status'),
      key: 'status',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      cell: ({ row }) => (
        <OrderStatus
          number={row.status}
          onClick={(e) => handleStatusClick(e, row)}
        />
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Start Date'),
      key: 'startDate',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <DateLabel date={row.startDate}></DateLabel>,
    },
    {
      header: t('Title'),
      key: 'title',
      width: '100%',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      onClick: (row) => navigate(`/orders/${row.id}`),
    },
    {
      header: t('Client'),
      key: 'recipient',
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      width: '270px',
      sortable: true, // enable sort function for column
      hiddenMobile: true,
      cell: ({ row }) => <ExoTableRecipient row={row} />, // custom xml for cells in the column. you get the row as object
      onClick: (row) =>
        navigate(`/${recipientTypeMap[row.recipientType]}/${row.recipient.id}`),
    },
  ];

  const filters = {
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'dropdown',
          label: t('Status'),
          key: 'state2',
          baseValue: 'status[eq]=',
          active: false,
          options: [
            {
              label: t('Created'),
              value: 0,
            },
            {
              label: t('Finished'),
              value: 1,
            },
            {
              label: t('Canceled'),
              value: 2,
            },
            {
              label: t('Sent'),
              value: 3,
            },
          ],
        },
      ],
    },
  };

  useEffect(() => {
    if (offerId) {
      handleNewOrder();
    }
  }, [offerId]);

  // actions
  const [openNewOrder, setOpenNewOrder] = useState(false);
  function handleNewOrder() {
    setOpenNewOrder(true);
  }
  return (
    <>
      <Header title={t('Orders')} />
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('New Order')}
          icon={<NoteAddIcon />}
          onClick={handleNewOrder}
        />
      </ExoTableToolbar>

      <ExoTable
        data={apiData}
        isLoading={!Boolean(apiData)}
        columns={Columns}
        extendElement={({ row, onUpdate }) => (
          <ExtendOrder row={row} onUpdate={onUpdate} />
        )}
        filters={filters}
      />

      <NewOrder
        open={openNewOrder}
        onClose={() => setOpenNewOrder(false)}
        orders={apiData}
        setOrders={setApiData}
        offerId={offerId}
      />
      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) => handleStatusChange(status, itemToChange)}
        customStatuses={[
          {
            label: t('Created'),
            status: 0,
            variant: t('open'),
          },
          {
            label: t('Finished'),
            status: 1,
            variant: t('finished'),
          },
          {
            label: t('Canceled'),
            status: 2,
            variant: t('rejected'),
          },
          {
            label: t('Sent'),
            status: 3,
            variant: t('sent'),
          },
        ]}
      />
    </>
  );
};

export default Orders;
